import AddressBook from "Components/address/AddressSelection"
import React, { useMemo, useState } from "react"
import "./AddressSelection.scss"
import { useSelector } from "react-redux"
import { RootState } from "store"
import { useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { processCollaborationMilestoneStep } from "services/api/collaboration"
import { SavedAddress } from "types"
import { Milestone, MilestoneStep } from "types/collaboration"
import inModal from "utils/hoc/inModal"

interface AddressSelectionProps {
    activeMilestone: Milestone,
    isActive: boolean,
    onClose: (refetch?: boolean) => void,
    step: MilestoneStep
}
const AddressSelection: React.FC<AddressSelectionProps> = ({ onClose, step }) => {

    const { campaignId, milestoneId, collaborationId } = useParams();
    const [submitting, setSubmitting] = useState(false);
    const hubId = useSelector((state: RootState) => state.auth.selectedHubId);
    const collaboration = useSelector((state: RootState) => state.legacyCollaborations.items[campaignId!]?.[collaborationId!]);
    const influencer = collaboration?.collaboration?.influencer;
    const [selectedAddress, setSelectedAddress] = useState<SavedAddress | null>(null);
    const [contact, setContact] = useState({ name: influencer?.name ?? '', mobile: influencer?.mobile ?? '' });
    const [errors, setErrors] = useState<{ name?: string; mobile?: string }>({});

    const validateContact = () => {
        const next: { name?: string; mobile?: string } = {};
        if (!contact.name.trim()) next.name = 'Name is required.';
        if (!contact.mobile.trim()) next.mobile = 'Mobile is required.';
        setErrors(next);
        return !next.name && !next.mobile;
    };

    const canSubmit =
        Boolean(selectedAddress) &&
        Boolean(contact.name.trim()) &&
        Boolean(contact.mobile.trim());

    const submitAddress = async () => {
        if (!campaignId || submitting || !selectedAddress || !collaborationId || !milestoneId || !hubId) return;
        if (!validateContact()) return;
        try {
            setSubmitting(true);
            await processCollaborationMilestoneStep(hubId, campaignId, collaborationId, milestoneId, { step: step.gig_step.slug, 'address': selectedAddress.uuid, name: contact.name, mobile: contact.mobile });
            onClose(true);
        } catch (error: any) {
            console.log(error);
            toast.error(error?.response?.data?.message ?? "Unknown error occurred.")
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="milestone-address-selection">
            <AddressBook
                onSelection={(address) => setSelectedAddress(address)}
            />
            {
                selectedAddress &&
                <div className="milestone-address-selection__contact">
                    <span className="milestone-address-selection__contact-title">Contact details</span>
                    <div className="address-form-group">
                        <label htmlFor="milestone-address-name">
                            Name <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            className={`form-control${errors.name ? ' is-invalid' : ''}`}
                            id="milestone-address-name"
                            value={contact.name}
                            placeholder="Full name"
                            onChange={(e) => {
                                setContact({ ...contact, name: e.target.value });
                                if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                            }}
                            onBlur={validateContact}
                        />
                        {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                    </div>
                    <div className="address-form-group">
                        <label htmlFor="milestone-address-mobile">
                            Mobile <span className="text-danger">*</span>
                        </label>
                        <input
                            type="tel"
                            className={`form-control${errors.mobile ? ' is-invalid' : ''}`}
                            id="milestone-address-mobile"
                            value={contact.mobile}
                            placeholder="10-digit mobile number"
                            onChange={(e) => {
                                setContact({ ...contact, mobile: e.target.value });
                                if (errors.mobile) setErrors((prev) => ({ ...prev, mobile: undefined }));
                            }}
                            onBlur={validateContact}
                        />
                        {errors.mobile && <div className="invalid-feedback d-block">{errors.mobile}</div>}
                    </div>
                </div>
            }
            <div className="milestone-address-selection__actions">
                <button type="button" className="theme-btn theme-btn-primary-outlined" onClick={() => onClose()}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="theme-btn theme-btn-primary"
                    disabled={!canSubmit || submitting}
                    onClick={submitAddress}
                >
                    Submit
                </button>
            </div>
        </div>
    )
}


const AddressSelectionModal: React.FC<AddressSelectionProps> = (props) => {
    const title = `Select Address`

    const ModalComponent = useMemo(
        () => inModal(AddressSelection, title, { modalBodyClassName: 'milestone-address-selection-modal-body' }),
        [title]
    )
    return <ModalComponent {...props} />
}


export default AddressSelectionModal;